import React, { useState } from 'react';
import { api } from '../api';
import toast from 'react-hot-toast';

export default function VideoRequestForm({ onRequested, embedded = false, heading = 'Add a video', submitLabel = 'Prepare video' }) {
  const [url, setUrl] = useState('');
  const [parts, setParts] = useState('');
  const [transcript, setTranscript] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(null);
  const [fromExtension, setFromExtension] = useState(null);   // note shown after an extension paste
  const [busy, setBusy] = useState(false);

  // The VideoQuiz Chrome extension copies a JSON package; pasting it fills in the link, title and transcript.
  function onTranscriptChange(value) {
    const text = value.trim();
    if (text.startsWith('{')) {
      try {
        const pkg = JSON.parse(text);
        if (pkg && pkg.videoquiz === 1 && typeof pkg.transcript === 'string') {
          setUrl(pkg.url || url);
          setTitle(pkg.title || '');
          setDuration(Number.isFinite(pkg.duration) && pkg.duration > 0 ? pkg.duration : null);
          setTranscript(pkg.transcript);
          setFromExtension(`Loaded ${pkg.lines || pkg.transcript.split('\n').length} transcript lines for “${pkg.title || pkg.url}”.`);
          return;
        }
      } catch { /* not JSON: treat as plain transcript text */ }
    }
    setFromExtension(null);
    setTranscript(value);
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { url: url.trim() };
      if (parts) body.parts = Number(parts);
      if (transcript.trim()) body.transcript = transcript;
      if (title) body.title = title;
      if (duration && transcript.trim()) body.duration = duration;
      const res = await api.requestVideo(body);
      toast.success(res.duplicate ? 'That video is already being prepared' : 'Requested. The worker will pick it up shortly.');
      setUrl(''); setParts(''); setTranscript(''); setTitle(''); setDuration(null); setFromExtension(null);
      onRequested(res);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  }

  return (
    <form className={embedded ? 'video-form' : 'video-card video-form'} onSubmit={submit}>
      {!embedded && <h3>{heading}</h3>}
      <label htmlFor={`${embedded ? 'w' : 'a'}-video-url`}>YouTube or Rumble link</label>
      <input id={`${embedded ? 'w' : 'a'}-video-url`} type="url" required value={url} onChange={e => setUrl(e.target.value)}
             placeholder="https://www.youtube.com/watch?v=…" autoComplete="off" />
      <label htmlFor={`${embedded ? 'w' : 'a'}-video-parts`}>Number of parts <span className="video-meta">(leave blank to let us choose)</span></label>
      <input id={`${embedded ? 'w' : 'a'}-video-parts`} type="number" min="1" max="200" inputMode="numeric" value={parts}
             onChange={e => setParts(e.target.value)} />
      <details className={embedded ? 'video-advanced' : undefined} open={!!fromExtension || undefined}>
        <summary>{embedded ? 'Advanced: use your own transcript' : 'No captions, or want to supply your own transcript? Paste it here'}</summary>
        <p className="video-meta">On YouTube open the video, choose <em>…more</em> then <em>Show transcript</em>, and copy the
          lines. Lines like <code>0:07 text</code> work.</p>
        <label htmlFor={`${embedded ? 'w' : 'a'}-video-transcript`}>Transcript</label>
        <textarea id={`${embedded ? 'w' : 'a'}-video-transcript`} rows="6" value={transcript} onChange={e => onTranscriptChange(e.target.value)} />
        {fromExtension && <p className="video-meta" role="status">{fromExtension}</p>}
      </details>
      <div className="video-actions" style={{ marginTop: 12 }}>
        <button className="btn-primary" type="submit" disabled={busy || !url.trim()}>
          {busy ? 'Requesting…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
